import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Order, Quote } from "@fprint/shared";
import { StatusBadge, buttonClasses } from "@fprint/ui";

import { claimQuote, listOrders, listQuotes } from "../lib/api";
import { SignOutButton, useAuth } from "../lib/auth";
import { formatEuros } from "../lib/money";
import { readResumeQuoteId } from "../lib/resumeQuote";

/** Customer-facing quote status. Never show the raw enum alone. */
function quoteStatusLabel(status: string): string {
  switch (status) {
    case "pending":
      return "Preparing quote";
    case "estimated":
      return "Estimated";
    case "ready":
      return "Ready to pay";
    case "in_review":
      return "In review";
    case "needs_manual_quote":
      return "Needs a manual quote";
    case "declined":
      return "Declined";
    case "failed":
      return "Quote failed";
    default:
      return "Quote";
  }
}

function quoteCta(status: string): string {
  if (status === "ready") return "Pay now";
  if (status === "declined" || status === "failed") return "View";
  return "Open quote";
}

function fmtDate(iso: string): string {
  // Guard against a missing/unparseable timestamp: `new Date(undefined)` (or a
  // bad string) is an Invalid Date whose toLocaleDateString() is the literal
  // "Invalid Date" — never show that to a customer. The shared Order schema
  // requires created_at, so this only bites malformed data, but the caller
  // renders the "placed …" suffix conditionally on a non-empty result.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function Orders() {
  const { isLoaded, getToken } = useAuth();
  const tokenGetter = useCallback(() => getToken(), [getToken]);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const justPlaced = params.get("placed");

  useEffect(() => {
    if (!isLoaded) return;
    const resumeId = readResumeQuoteId();
    const load = async () => {
      // Existing-account sign-in already works: never let quote claim/list
      // failures hide the orders list (the working path).
      try {
        setOrders(await listOrders(tokenGetter));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return;
      }
      if (resumeId) {
        try {
          await claimQuote(resumeId, tokenGetter);
        } catch {
          // 404 = already someone else's, or unknown. Orders still show.
        }
      }
      try {
        setQuotes(await listQuotes(tokenGetter));
      } catch {
        setQuotes([]);
      }
    };
    void load();
  }, [isLoaded, tokenGetter]);

  const orderedQuoteIds = new Set((orders ?? []).map((o) => o.quote_id));
  const visibleQuotes = (quotes ?? []).filter((q) => !orderedQuoteIds.has(q.id));

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col gap-6 px-6 py-12">
      <header
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        data-testid="orders-header"
      >
        <h1 className="text-3xl font-bold">My orders</h1>
        <div
          className="flex w-full max-w-sm flex-col gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center"
          data-testid="orders-cta-row"
        >
          <Link
            to="/quote"
            className={buttonClasses({
              variant: "secondary",
              className: "w-full sm:w-auto",
            })}
          >
            New quote
          </Link>
          <SignOutButton className="w-full sm:w-auto" />
        </div>
      </header>

      {justPlaced && (
        <div
          role="status"
          className="rounded-md border border-success bg-success/10 px-4 py-3 text-sm text-success-fg"
          data-testid="order-placed-banner"
        >
          Order <strong data-testid="placed-order-no">{justPlaced}</strong>{" "}
          placed.
        </div>
      )}

      {error && (
        <p className="text-danger-fg" role="alert">
          {error}
        </p>
      )}

      {(!isLoaded || orders === null) && !error && (
        <p className="text-muted-fg">Loading…</p>
      )}

      {visibleQuotes.length > 0 && (
        <section className="flex flex-col gap-3" data-testid="pending-quotes">
          <h2 className="text-lg font-semibold">Quotes</h2>
          <ul className="flex flex-col gap-3">
            {visibleQuotes.map((q) => (
              <li
                key={q.id}
                className="flex items-center justify-between rounded-md border border-border bg-bg px-4 py-3"
                data-testid={`pending-quote-${q.id}`}
              >
                <div className="flex flex-col gap-1">
                  <span className="font-medium text-sm">
                    {quoteStatusLabel(q.status)}
                  </span>
                  {q.result?.price_cents != null && (
                    <span className="text-xs text-muted-fg">
                      {formatEuros(q.result.price_cents)}
                    </span>
                  )}
                </div>
                <Link
                  to={`/order/${q.id}`}
                  className={buttonClasses({ size: "sm" })}
                >
                  {quoteCta(q.status)}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {orders && orders.length === 0 && visibleQuotes.length === 0 && (
        <p className="text-muted-fg" data-testid="orders-empty">
          No orders yet.
        </p>
      )}

      {orders && orders.length > 0 && (
        <>
          <p className="text-sm text-muted-fg" data-testid="orders-email-note">
            We&apos;ll email you at each step — when printing starts, and when
            your order ships or is ready for pickup.
          </p>
          <ul className="flex flex-col gap-3" data-testid="orders-list">
            {orders.map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between rounded-md border border-border bg-bg px-4 py-3"
              >
                <div className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <span
                      className="font-mono text-sm"
                      data-testid={`order-no-${o.order_no}`}
                    >
                      {o.order_no}
                    </span>
                    <StatusBadge status={o.status} />
                  </span>
                  <span className="text-xs text-muted-fg">
                    Qty {o.qty}
                    {fmtDate(o.created_at) &&
                      ` · placed ${fmtDate(o.created_at)}`}
                  </span>
                </div>
                <span className="font-medium">
                  {formatEuros(o.price_charged_cents)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </main>
  );
}
